import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { cubicBezier } from "@/lib/cubicBezier";

const EASE_OUT = cubicBezier(0.23, 1, 0.32, 1);
const DURATION_MS = 250;
const GAP_PX = 8;

// The row's scroll position with its sticky group header still in view above it.
function targetY(row: HTMLElement): number {
  const header = row
    .closest("section")
    ?.querySelector<HTMLElement>("button[aria-expanded]");
  const above = header
    ? parseFloat(getComputedStyle(header).top) + header.offsetHeight
    : 0;
  return Math.max(
    0,
    row.getBoundingClientRect().top + window.scrollY - above - GAP_PX,
  );
}

export function useFollowRow(orderedIds: string[], expandedId: string | null) {
  const [cursor, setCursor] = useState<string | null>(null);
  const currentId = expandedId ?? cursor;

  useEffect(() => {
    if (expandedId) setCursor(expandedId);
  }, [expandedId]);

  const scrolledTo = useRef<string | null>(null);
  const held = useRef<{ id: string; top: number } | null>(null);
  const running = useRef<{ id: string; frame: number } | null>(null);
  const stop = () => {
    if (running.current) cancelAnimationFrame(running.current.frame);
    running.current = null;
  };
  // Keep the reviewed row where the glide left it while the queue above changes.
  const hold = () => {
    const at = held.current;
    if (!at || running.current) return;
    const row = document.getElementById(`attribute-review-row-${at.id}`);
    const shift = row ? row.getBoundingClientRect().top - at.top : 0;
    if (Math.abs(shift) >= 1)
      window.scrollTo({
        top: window.scrollY + shift,
        behavior: "instant" as ScrollBehavior,
      });
  };

  useEffect(() => {
    if (held.current?.id !== currentId) held.current = null;
    const row = currentId
      ? document.getElementById(`attribute-review-row-${currentId}`)
      : null;
    if (!row || !currentId || scrolledTo.current === currentId) return;
    // A queue update mid-scroll must not restart it: the frames below already re-aim.
    if (running.current?.id === currentId) return;
    stop();
    const reduce = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const startY = window.scrollY;
    const started = performance.now();
    const finish = () => {
      stop();
      scrolledTo.current = currentId;
      const row = document.getElementById(`attribute-review-row-${currentId}`);
      held.current = row
        ? { id: currentId, top: row.getBoundingClientRect().top }
        : null;
    };
    // Re-aimed every frame: opening the drawer narrows the queue and moves the row mid-scroll.
    const step = (now: number) => {
      const target = document.getElementById(
        `attribute-review-row-${currentId}`,
      );
      if (!target) return finish();
      const progress = reduce ? 1 : Math.min(1, (now - started) / DURATION_MS);
      // index.css sets scroll-behavior: smooth; each step must jump or the browser eases it again.
      window.scrollTo({
        top: startY + (targetY(target) - startY) * EASE_OUT(progress),
        behavior: "instant" as ScrollBehavior,
      });
      if (progress < 1 && running.current)
        running.current.frame = requestAnimationFrame(step);
      else finish();
    };
    running.current = { id: currentId, frame: requestAnimationFrame(step) };
  }, [currentId, orderedIds.length]);

  // A render moves rows between groups; an approved row then shrinks away over frames (reviewMotion.css).
  useLayoutEffect(hold);
  useEffect(() => {
    const resized = new ResizeObserver(hold);
    resized.observe(document.body);
    return () => resized.disconnect();
  }, []);

  useEffect(() => {
    const userScroll = () => {
      if (running.current) scrolledTo.current = running.current.id;
      held.current = null;
      stop();
    };
    window.addEventListener("wheel", userScroll, { passive: true });
    window.addEventListener("touchstart", userScroll, { passive: true });
    return () => {
      stop();
      window.removeEventListener("wheel", userScroll);
      window.removeEventListener("touchstart", userScroll);
    };
  }, []);

  return currentId;
}
